import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { post, put } from "@common/api/apiShared";
import { useTranslation } from "@localization/useTranslation";
import { isNullOrWhitespace } from "@common/stringOperations";
import { validateEmail } from "@common/emailValidator";
import { Result } from "@common/api/result";
import { ExternalUserSaveModel } from "../ExternalUser.types";
import { IAddEditExternalUserDialogProps } from "./AddEditExternalUserDialog";
import { SelectableColoredItem } from "../../processingActivity/ProcessingActivity.types.ts";

export function useAddEditExternalUserDialogHook(props: IAddEditExternalUserDialogProps) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();

    const isEdit = props.externalUser !== undefined;

    let { data: businessAreaOptions, isLoading: businessAreaIsLoading } = useDotLegalQueryWithKey<Array<SelectableColoredItem>>(
        "selectableBusinessAreas",
        "/BusinessAreas/businessareas"
    );

    const [saveModel, setSaveModel] = useState<ExternalUserSaveModel>({
        fullName: props.externalUser?.fullName ?? "",
        email: props.externalUser?.email ?? "",
        businessAreas: props.externalUser?.businessAreas.map((x) => x.id) ?? [],
    });
    const [showErrors, setShowErrors] = useState(false);
    // Set by the server rather than by the model, so it cannot be derived — and it is dropped the moment the
    // email changes, since the rejected address is no longer the one in the field.
    const [emailInUseError, setEmailInUseError] = useState("");

    const saveMutation = useDotLegalMutation(save);
    const editMutation = useDotLegalMutation(update);

    const modelUpdater = {
        onEmailChange(email: string) {
            setEmailInUseError("");
            setSaveModel({ ...saveModel, email });
        },
        onFullNameChange(fullName: string) {
            setSaveModel({ ...saveModel, fullName });
        },
        onBusinessAreasChange(businessAreas: Array<string>) {
            setSaveModel({ ...saveModel, businessAreas });
        },
    };

    function getFullNameError() {
        return isNullOrWhitespace(saveModel.fullName) ? translateString("fieldRequired") : "";
    }

    function getEmailError() {
        if (isNullOrWhitespace(saveModel.email)) return translateString("fieldRequired");
        if (!validateEmail(saveModel.email.trim())) return translateString("invalidEmail");

        return "";
    }

    const isValid = getFullNameError() === "" && getEmailError() === "";

    // Derived rather than stored, so a message disappears as soon as the field it complains about is filled in
    // instead of waiting for the next save attempt.
    const errors = {
        fullNameError: showErrors ? getFullNameError() : "",
        emailError: emailInUseError || (showErrors ? getEmailError() : ""),
    };

    function onSave() {
        setShowErrors(true);
        if (!isValid) return;

        if (isEdit) updateExternalUser();
        else createExternalUser();
    }

    function createExternalUser() {
        saveMutation.mutateAsync(undefined, {
            onSuccess: (result) => {
                if (isDuplicateEmail(result)) return;
                onSaved(result.value().id);
            },
        });
    }

    function updateExternalUser() {
        editMutation.mutateAsync(undefined, {
            onSuccess: (result) => {
                if (isDuplicateEmail(result)) return;
                onSaved(props.externalUser!.id);
            },
        });
    }

    function isDuplicateEmail(result: Result<unknown>) {
        if (!result.hasError("externalUserExistWithEmail")) return false;

        setEmailInUseError(translateString("externalUserExistWithEmail"));
        return true;
    }

    function onSaved(externalUserId: string) {
        snackbar.show(createElement(Trans, { i18nKey: "externalUserSaved", values: { name: saveModel.fullName } }));
        props.onSave(externalUserId);
    }

    function save() {
        return post<{ id: string }>("/external-users", saveModel);
    }

    function update() {
        return put(`/external-users/${props.externalUser!.id}`, saveModel);
    }

    return {
        isEdit,
        saveModel,
        modelUpdater,
        errors,
        inProgress: saveMutation.isLoading || editMutation.isLoading,
        onSave,
        businessAreaOptions,
        businessAreaIsLoading,
    };
}
